import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, RefreshCw, Clock, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Search, Filter, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { relativeTime } from '@/lib/time-format'

interface SyncLog {
  id: string
  sync_type: string
  season: string
  matchdays_synced: number[]
  fixtures_updated: number
  results_updated: number
  selections_processed: number
  errors: string[] | null
  duration_ms: number
  created_at: string
}

const SYNC_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'fixtures', label: 'Fixtures' },
  { value: 'results', label: 'Results' },
  { value: 'selections', label: 'Selections' },
]

const PAGE_SIZE = 15

export default function AdminLogs() {
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null)
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [matchdaySearch, setMatchdaySearch] = useState<string>('')
  const [page, setPage] = useState(0)

  // Fetch sync logs with pagination and filters
  const { data, isLoading: isLoadingLogs, refetch: refetchLogs } = useQuery({
    queryKey: ['sync-logs', page, typeFilter, matchdaySearch],
    queryFn: async () => {
      let query = supabase
        .from('sync_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      if (typeFilter) {
        query = query.eq('sync_type', typeFilter)
      }

      if (matchdaySearch) {
        const md = parseInt(matchdaySearch, 10)
        if (!isNaN(md)) {
          query = query.contains('matchdays_synced', [md])
        }
      }

      const { data, error, count } = await query
      if (error) throw error
      return { logs: (data ?? []) as SyncLog[], totalCount: count ?? 0 }
    },
    refetchInterval: 60000, // Refetch every minute
  })

  const syncLogs = data?.logs ?? []
  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="p-2 -ml-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Sync Logs</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
              {totalCount} total logs
            </p>
          </div>
          <button
            onClick={() => refetchLogs()}
            className="p-2 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          {/* Matchday search */}
          <div className="relative flex-1 min-w-[120px] max-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="number"
              min={1}
              placeholder="Matchday..."
              value={matchdaySearch}
              onChange={(e) => {
                setMatchdaySearch(e.target.value)
                setPage(0) // Reset to first page on search
              }}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                         placeholder:text-zinc-400 dark:placeholder:text-zinc-500
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
          </div>

          {/* Type filter */}
          <div className="relative min-w-[140px]">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value)
                setPage(0) // Reset to first page on filter change
              }}
              className="w-full pl-9 pr-8 py-2 text-sm rounded-xl border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/40 appearance-none"
            >
              {SYNC_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
          </div>
        </div>

        {/* Logs list */}
        {isLoadingLogs ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
          </div>
        ) : syncLogs.length === 0 ? (
          <div className="text-center py-12 text-sm text-zinc-500 dark:text-zinc-400">
            {typeFilter || matchdaySearch
              ? 'No logs match your filters.'
              : 'No sync logs yet. Logs will appear here after the first automated sync runs.'}
          </div>
        ) : (
          <div className="space-y-2">
            {syncLogs.map((log) => {
              const hasErrors = log.errors && log.errors.length > 0
              const isExpanded = expandedLogId === log.id
              const date = new Date(log.created_at)
              const timeAgo = relativeTime(log.created_at)

              return (
                <div
                  key={log.id}
                  className={`rounded-xl border transition-colors ${
                    hasErrors
                      ? 'border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20'
                      : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900'
                  }`}
                >
                  <button
                    onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left"
                  >
                    {/* Status icon */}
                    {hasErrors ? (
                      <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                    )}

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          MD {log.matchdays_synced.join(', ')}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">
                          {log.sync_type}
                        </span>
                        <span className="text-xs text-zinc-400 dark:text-zinc-500">
                          {log.season}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                        <span>{log.fixtures_updated} fixtures</span>
                        <span>{log.results_updated} results</span>
                        <span>{log.selections_processed ?? 0} selections</span>
                      </div>
                    </div>

                    {/* Time + expand */}
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {timeAgo}
                        </div>
                        <div className="text-[10px] text-zinc-400 dark:text-zinc-500">
                          {log.duration_ms}ms
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-zinc-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-zinc-400" />
                      )}
                    </div>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 pb-3 pt-0 border-t border-zinc-100 dark:border-zinc-800">
                      <div className="mt-3 space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">ID</span>
                          <span className="text-zinc-700 dark:text-zinc-300 font-mono text-[10px]">
                            {log.id}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">Timestamp</span>
                          <span className="text-zinc-700 dark:text-zinc-300 font-mono">
                            {date.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">Sync Type</span>
                          <span className="text-zinc-700 dark:text-zinc-300">
                            {log.sync_type}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">Fixtures Updated</span>
                          <span className="text-zinc-700 dark:text-zinc-300 font-mono">
                            {log.fixtures_updated}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">Results Updated</span>
                          <span className="text-zinc-700 dark:text-zinc-300 font-mono">
                            {log.results_updated}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">Selections Processed</span>
                          <span className="text-zinc-700 dark:text-zinc-300 font-mono">
                            {log.selections_processed ?? 0}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-500 dark:text-zinc-400">Duration</span>
                          <span className="text-zinc-700 dark:text-zinc-300 font-mono">
                            {log.duration_ms}ms
                          </span>
                        </div>
                        {hasErrors && (
                          <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-800/50">
                            <span className="text-red-600 dark:text-red-400 font-medium">Errors:</span>
                            <ul className="mt-1 space-y-1">
                              {log.errors?.map((err, i) => (
                                <li key={i} className="text-red-600 dark:text-red-400 font-mono break-all">
                                  {err}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-zinc-200 dark:border-zinc-800">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg
                         text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            <span className="text-sm text-zinc-500 dark:text-zinc-400">
              Page {page + 1} of {totalPages}
            </span>

            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg
                         text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800
                         disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

